import { Building2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { FacilityCard } from './FacilityCard';
import { FacilityCardSkeleton } from './FacilityCardSkeleton';
import {
  useGetOrganizationFacilitiesPage,
  useGetTransportVehicles,
  type OrganizationFacilitiesFilters,
} from '~/api/facility';
import type {
  GetOrganizationFacilitiesPageQuery,
  TransportVehicle,
} from '~/graphql/graphql';
import { Button } from '~/components/ui';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { UpsertFacilityModal } from './UpsertFacilityModal';
import { ListToolbar } from './ListToolbar';
import type { Facility } from './OrganizationFacilities';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import type { PaginatedResponse } from '~/types';

const PAGE_SIZE = 20;

export function OrganizationAddresses() {
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const { searchInputValue, activeSearch, handleSearchChange } =
    useDebouncedSearchInput();
  const debouncedSearch = activeSearch.trim();

  const { t } = useTranslation('settings');

  const queryParams = useMemo<OrganizationFacilitiesFilters>(
    () => ({ search: debouncedSearch || undefined }),
    [debouncedSearch]
  );

  const {
    data: facilities,
    sentinelRef,
    isLoading,
    isLoadingMore,
    hasMoreData,
    pagination,
    removeItem,
    error,
    refetch,
  } = usePaginatedQuery<
    GetOrganizationFacilitiesPageQuery,
    Facility,
    OrganizationFacilitiesFilters
  >({
    fetchHook: useGetOrganizationFacilitiesPage,
    select: (data) =>
      data?.organizationFacilitiesPage as PaginatedResponse<Facility>,
    options: { pageSize: PAGE_SIZE, filters: queryParams },
  });

  const { data: vehiclesData, isLoading: vehiclesLoading } =
    useGetTransportVehicles();

  const allVehicles = vehiclesData?.transportVehicles ?? [];
  const isInitialLoading = isLoading || vehiclesLoading;
  const isUnfilteredView = debouncedSearch === '';
  const noResults = !isInitialLoading && facilities.length === 0;
  // The backend blocks deleting an org's only facility. Enforce that in the UI only in the
  // unfiltered view where totalElements is the true org count; under search it's a subset,
  // so leave delete enabled and let the backend be the authority.
  const canDeleteFacility = isUnfilteredView
    ? (pagination?.totalElements ?? 0) > 1
    : true;

  return (
    <section
      className="w-full bg-neut-bg-alt rounded-lg border border-neut-200 p-6 shadow-sm"
      data-testid="addresses-section"
    >
      <div className="flex justify-between items-start gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-prim-100 rounded-lg">
            <Building2 size={20} className="text-prim" />
          </div>
          <div>
            <h2
              className="text-neut-prim font-semibold"
              data-testid="facilities-heading"
            >
              {t('settings.facilities.title')}
            </h2>
            <p className="text-sm text-neut-sec mt-1">
              {t('settings.facilities.description')}
            </p>
          </div>
        </div>
        <Button
          data-testid="add-facility-button"
          onClick={() => setShowFacilityModal(true)}
          width="auto"
          size="sm"
        >
          <Plus size={16} /> {t('settings.facilities.add_button')}
        </Button>
      </div>

      <ListToolbar
        searchTerm={searchInputValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t('settings.facilities.search_placeholder')}
        searchTestId="facility-search"
      />

      {isInitialLoading ? (
        <FacilityCardSkeleton />
      ) : error ? (
        <RetryBanner
          message={t('settings.facilities.load_failed')}
          retryLabel={t('settings.facilities.retry')}
          onRetry={() => refetch()}
          testId="facilities"
        />
      ) : noResults ? (
        <p
          className="text-sm text-neut-sec text-center py-6"
          data-testid="facilities-empty-state"
        >
          {isUnfilteredView
            ? t('settings.facilities.no_facilities')
            : t('settings.facilities.no_results')}
        </p>
      ) : (
        <>
          <Facilities
            facilities={facilities}
            allVehicles={allVehicles}
            canDelete={canDeleteFacility}
            onDeleted={removeItem}
          />
          {isLoadingMore && hasMoreData && <FacilityCardSkeleton />}
          <div
            ref={sentinelRef}
            data-testid="facilities-scroll-sentinel"
            style={{ height: '1px' }}
          />
        </>
      )}

      <UpsertFacilityModal
        isOpen={showFacilityModal}
        onClose={() => setShowFacilityModal(false)}
        allVehicles={allVehicles}
      />
    </section>
  );
}

function Facilities({
  facilities,
  allVehicles,
  canDelete,
  onDeleted,
}: {
  facilities: Facility[];
  allVehicles: TransportVehicle[];
  canDelete: boolean;
  onDeleted: (id: string) => void;
}) {
  return (
    <div className="space-y-3">
      {facilities.map((facility: Facility) => (
        <FacilityCard
          key={facility.id}
          facility={facility}
          allVehicles={allVehicles}
          canDelete={canDelete}
          onDeleted={onDeleted}
        />
      ))}
    </div>
  );
}
